import { useState, useEffect } from 'react';
import { 
  ShieldCheckIcon, 
  KeyIcon, 
  CommandLineIcon, 
  DocumentArrowDownIcon,
  ArrowRightCircleIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  EyeIcon,
  LockClosedIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../libs/apiUtils';
import Pagination from '../components/Pagination';
import { Meta } from '../types/global';

interface AccessibleTable {
  id: string;
  name: string;
  description: string;
  isPublic: boolean;
  categoryId: string;
  createdByName?: string;
}

export default function ConsumerDashboard() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tables, setTables] = useState<AccessibleTable[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(6);
  const [loading, setLoading] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin ke papan klip');
  };

  const loginEndpoint = `${process.env.REACT_APP_BACKEND_URL}/api/login`;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    async function loadTables() {
      try {
        setLoading(true);
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/tables`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page,
            perPage,
            search: debouncedSearch.trim() || undefined
          })
        });

        const data = await res.json();
        if (res.ok && data.success) {
          setTables(data.data || []);
          setMeta(data.meta || null);
        } else {
          toast.error(data.message || 'Gagal memuat daftar tabel');
        }
      } catch (err) {
        toast.error('Gagal terhubung ke server');
      } finally {
        setLoading(false);
      }
    }

    loadTables();
  }, [page, perPage, debouncedSearch]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center size-16 bg-primary-100 rounded-2xl text-primary-600 mb-2 shadow-inner">
          <ShieldCheckIcon className="size-10" />
        </div>
        <h1 className="text-4xl font-extrabold text-neutral-900 tracking-tight">Pusat Integrasi Data</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
          Selamat datang di dashboard konsumen. Cari tabel yang Anda miliki atau memiliki akses, dan gunakan panduan integrasi untuk menghubungkan data secara aman.
        </p>
      </div>

      {/* Interactive Table Search Utility */}
      <div className="bg-white rounded-3xl p-8 shadow-xl shadow-neutral-100 border border-neutral-100 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <TableCellsIcon className="size-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-neutral-900">Pencarian Tabel Akses Anda</h2>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Menampilkan tabel publik serta tabel yang Anda buat atau telah diberikan izin akses khusus.
            </p>
          </div>

          <div className="relative w-full sm:w-72">
            <MagnifyingGlassIcon className="size-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nama tabel..."
              className="w-full pl-10 pr-8 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-800 placeholder-neutral-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-neutral-400 hover:text-neutral-600 bg-neutral-200 rounded-full px-1.5 py-0.5"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-3 text-sm text-neutral-500">Mencari tabel...</p>
          </div>
        ) : tables.length === 0 ? (
          <div className="py-12 text-center bg-neutral-50 rounded-2xl border border-dashed border-neutral-200">
            <TableCellsIcon className="size-10 text-neutral-300 mx-auto mb-2" />
            <p className="text-neutral-700 font-semibold">Tabel Tidak Ditemukan</p>
            <p className="text-xs text-neutral-500 max-w-sm mx-auto mt-1">
              {searchQuery ? `Tidak ada tabel yang cocok dengan kata kunci "${searchQuery}".` : 'Anda belum memiliki akses ke tabel manapun.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tables.map((tbl) => (
              <div
                key={tbl.id}
                className="p-5 rounded-2xl border border-neutral-100 hover:border-primary-200 bg-neutral-50/50 hover:bg-primary-50/20 transition-all flex flex-col justify-between group shadow-sm hover:shadow"
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-neutral-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {tbl.name}
                    </h3>
                    {tbl.isPublic ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 shrink-0">
                        <GlobeAltIcon className="size-3" /> Publik
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 shrink-0">
                        <LockClosedIcon className="size-3" /> Akses Khusus
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 line-clamp-2 leading-relaxed">
                    {tbl.description || 'Tidak ada deskripsi tersedia.'}
                  </p>
                </div>

                <div className="pt-4 mt-4 border-t border-neutral-100 flex items-center justify-between">
                  <span className="text-[11px] text-neutral-400">
                    Oleh: {tbl.createdByName || '-'}
                  </span>
                  <button
                    onClick={() => navigate(`/data/${tbl.id}`)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary-600 hover:text-primary-700 bg-white hover:bg-primary-50 border border-neutral-200 hover:border-primary-200 px-3 py-1.5 rounded-lg transition-all shadow-sm"
                  >
                    <EyeIcon className="size-3.5" /> Lihat Data
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {meta && meta.lastPage > 1 && (
          <div className="pt-4 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Step 1 */}
        <div className="bg-white rounded-3xl p-8 shadow-xl shadow-neutral-100 border border-neutral-100 flex flex-col md:flex-row gap-8">
          <div className="md:w-1/3 space-y-4">
            <div className="size-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 font-bold text-xl">1</div>
            <h3 className="text-2xl font-bold text-neutral-900">Autentikasi</h3>
            <p className="text-neutral-500 leading-relaxed">
              Langkah pertama adalah melakukan login menggunakan kredensial akun Anda untuk mendapatkan <span className="font-bold text-neutral-900">Bearer Token</span>.
            </p>
          </div>
          <div className="md:w-2/3 space-y-4">
            <div className="bg-neutral-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-neutral-800 px-4 py-2 flex items-center justify-between border-b border-neutral-700">
                <div className="flex items-center gap-2">
                  <CommandLineIcon className="size-4 text-neutral-400" />
                  <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Request</span>
                </div>
                <button onClick={() => copyToClipboard(`curl -X POST ${loginEndpoint} -H "Content-Type: application/json" -d '{"email":"your@email.com","password":"yourpassword"}'`)} className="text-xs text-primary-400 hover:text-primary-300 font-medium">Copy cURL</button>
              </div>
              <div className="p-5 font-mono text-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-green-400 font-bold">POST</span>
                  <span className="text-primary-300 break-all">{loginEndpoint}</span>
                </div>
                <pre className="text-neutral-400">
{`{
  "email": "youremail@test.com",
  "password": "yourpassword"
}`}
                </pre>
              </div>
            </div>
            
            <div className="bg-emerald-950/20 rounded-2xl p-5 border border-emerald-500/20">
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-wider mb-3">
                <ArrowRightCircleIcon className="size-4" />
                Response (Success)
              </div>
              <pre className="text-emerald-800 font-mono text-xs overflow-x-auto">
{`{
  "user": { "fullName": "Account Name", "role": "consumer" },
  "token": "oat_xxxxx.yyyyy.zzzzz",
  "expiresAt": "2025-09-17T09:13:20Z"
}`}
              </pre>
            </div>
          </div>
        </div>

        {/* Step 2 & 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-3xl p-8 shadow-xl shadow-neutral-100 border border-neutral-100 space-y-6">
            <div className="size-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 font-bold text-xl">2</div>
            <h3 className="text-2xl font-bold text-neutral-900">Gunakan Token</h3>
            <p className="text-neutral-500 leading-relaxed">
              Sertakan token yang Anda dapatkan di setiap permintaan API selanjutnya di dalam header <span className="font-bold text-neutral-900">Authorization</span>.
            </p>
            <div className="bg-neutral-50 p-4 rounded-2xl border border-neutral-200 font-mono text-sm text-neutral-700">
              <div className="text-xs font-bold text-neutral-400 mb-1 uppercase">Header</div>
              Authorization: Bearer <span className="text-primary-600">YOUR_TOKEN</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-xl shadow-neutral-100 border border-neutral-100 space-y-6">
            <div className="size-12 bg-rose-100 rounded-xl flex items-center justify-center text-rose-600 font-bold text-xl">3</div>
            <h3 className="text-2xl font-bold text-neutral-900">Kelola Sesi</h3>
            <p className="text-neutral-500 leading-relaxed">
              Token memiliki masa berlaku (<span className="italic">Expiration</span>). Perhatikan field <span className="font-bold text-neutral-900">expiresAt</span> untuk menjadwalkan autentikasi ulang secara otomatis.
            </p>
            <div className="flex items-center gap-4 p-4 bg-rose-50 rounded-2xl border border-rose-100">
              <KeyIcon className="size-10 text-rose-500" />
              <div className="text-sm text-rose-700 font-medium leading-tight">
                Gunakan token hanya untuk satu aplikasi yang terdaftar. Jangan bagikan token Anda.
              </div>
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="bg-indigo-600 rounded-[3rem] p-12 text-white shadow-2xl shadow-indigo-200 relative overflow-hidden">
          <div className="relative z-10 space-y-6">
            <h2 className="text-3xl font-extrabold">Siap untuk Memulai?</h2>
            <p className="text-indigo-100 max-w-xl text-lg">
              Jelajahi daftar tabel yang tersedia di atas atau di menu samping untuk melihat dokumentasi spesifik setiap endpoint dan format data yang tersedia.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20">
                <DocumentArrowDownIcon className="size-6" />
                <span className="font-bold">JSON Format Standard</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/20">
                <CommandLineIcon className="size-6" />
                <span className="font-bold">RESTful Architecture</span>
              </div>
            </div>
          </div>
          {/* Decorative circles */}
          <div className="absolute -top-24 -right-24 size-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 size-64 bg-indigo-400/20 rounded-full blur-3xl"></div>
        </div>
      </div>
    </div>
  );
}
